import { differenceInHours, format, parseISO } from 'date-fns'

// Centralized date-fns entry point. Call sites use these intent-named
// formatters and never import date-fns directly, so if we ever swap the
// underlying date library it's a one-file change here.
//
// Use parseISO (not `new Date(str)`) for date-only strings like "1990-01-01":
// `new Date` parses them as UTC midnight, which renders as the previous day in
// negative-offset timezones. parseISO treats them as local time.

// "1990-01-01" → "Jan 1, 1990"
export function formatDateShort(value: string): string {
  const date = parseISO(value)
  return isNaN(date.getTime()) ? value : format(date, 'MMM d, yyyy')
}

// Calendar dates can arrive as ISO timestamps (e.g. a repayment date at UTC
// midnight). Preserve the supplied day instead of converting it to local time.
export function formatCalendarDateShort(value: string): string {
  const date = parseISO(value.split('T')[0])
  return isNaN(date.getTime()) ? value : format(date, 'MMM d, yyyy')
}

// "2028-04-01" → "04/2028" (e.g. card expiry)
export function formatMonthYear(value: string): string {
  const date = parseISO(value)
  return isNaN(date.getTime()) ? value : format(date, 'MM/yyyy')
}

// True when an ISO timestamp falls inside the last `hours` before `now`.
// Unparseable input is "not recent"; a future timestamp counts as recent
// (there is nothing left to wait out).
export function isWithinHours({
  value,
  hours,
  now = new Date(),
}: {
  value: string
  hours: number
  now?: Date
}): boolean {
  const date = parseISO(value)
  if (isNaN(date.getTime())) return false
  return differenceInHours(now, date) < hours
}
